import type {
  BugListResponse,
  CreateBugResponse,
  AreaPath,
  IterationPath,
  BugDetail,
  BugCommentsResponse,
  BugComment,
  BugHistoryEntry,
  TeamMember,
  ParentStoryOption,
  CustomerConversation,
  CustomerTicketFlag,
} from './types';
import type { BugWorkflow } from './state-reasons';
import { TICKET_UPLOAD_FIELDS } from '@microservices/dtos/lib/ticket/ticket.constants';
import { assertAuthorizedOrClearSession } from '@/infrastructure/auth/unauthorized-session-bridge';

export async function fetchAreas(): Promise<AreaPath[]> {
  const response = await fetch('/api/bugs/areas', { credentials: 'include' });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch areas: ${response.status}`);
  const data = await response.json();
  return data.areas;
}

export async function fetchIterations(): Promise<IterationPath[]> {
  const response = await fetch('/api/bugs/iterations', {
    credentials: 'include',
    signal: AbortSignal.timeout(30_000),
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch iterations: ${response.status}`);
  const data = await response.json();
  return data.iterations;
}

export async function createBugReport(formData: FormData): Promise<CreateBugResponse> {
  const response = await fetch('/api/bugs', {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message ?? `Failed to create bug report: ${response.status}`);
  }
  return response.json();
}

/**
 * Sortable bug-list columns. Most map to WIQL ORDER BY fields (id, title,
 * dates, state, priority, reason, area); people columns and the parent story
 * sort in-process on the derived values the list renders (reporter tag /
 * assignee display name / parent User Story title).
 */
export type BugListSortColumn =
  | 'id'
  | 'title'
  | 'createdDate'
  | 'changedDate'
  | 'createdBy'
  | 'assignedTo'
  | 'state'
  | 'priority'
  | 'reason'
  | 'area'
  | 'iteration'
  | 'parentStory';

export interface BugListQueryParams {
  page?: number;
  pageSize?: number;
  /** Exact work-item id lookup (Azure DevOps `System.Id`). */
  id?: number;
  state?: string[];
  priority?: number[];
  areaPath?: string;
  tag?: string;
  title?: string;
  reason?: string;
  assignee?: string;
  /** Only bugs with no assignee (`System.AssignedTo` empty); overrides `assignee`. */
  unassigned?: boolean;
  reporter?: string;
  /** Filter by iteration path (Azure DevOps `System.IterationPath`). */
  iterationPath?: string;
  /** Filter by a single parent User Story id (Azure DevOps `System.Parent`). */
  parentStory?: number;
  /** Sort column; defaults to `createdDate` on the server. */
  sortBy?: BugListSortColumn;
  sortOrder?: 'asc' | 'desc';
}

export async function fetchBugs(params: BugListQueryParams): Promise<BugListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params.id) searchParams.set('id', String(params.id));
  if (params.state && params.state.length > 0) searchParams.set('state', params.state.join(','));
  if (params.priority && params.priority.length > 0) searchParams.set('priority', params.priority.join(','));
  if (params.areaPath) searchParams.set('areaPath', params.areaPath);
  if (params.tag) searchParams.set('tag', params.tag);
  if (params.title) searchParams.set('title', params.title);
  if (params.reason) searchParams.set('reason', params.reason);
  if (params.assignee) searchParams.set('assignee', params.assignee);
  if (params.unassigned) searchParams.set('unassigned', 'true');
  if (params.reporter) searchParams.set('reporter', params.reporter);
  if (params.iterationPath) searchParams.set('iterationPath', params.iterationPath);
  if (params.parentStory) searchParams.set('parentStory', String(params.parentStory));
  if (params.sortBy) searchParams.set('sortBy', params.sortBy);
  if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder);

  const response = await fetch(`/api/bugs?${searchParams}`, { credentials: 'include' });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch bugs: ${response.status}`);
  return response.json();
}

export async function fetchParentStories(search?: string): Promise<ParentStoryOption[]> {
  const searchParams = new URLSearchParams();
  if (search) searchParams.set('search', search);

  const response = await fetch(`/api/bugs/parent-stories?${searchParams}`, {
    credentials: 'include',
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch parent stories: ${response.status}`);
  const data = await response.json();
  return data.stories;
}

export async function fetchBugDetail(id: number): Promise<BugDetail> {
  const response = await fetch(`/api/bugs/${id}`, { credentials: 'include' });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch bug detail: ${response.status}`);
  return response.json();
}

export async function claimBug(id: number, assignedTo: string): Promise<void> {
  const response = await fetch(`/api/bugs/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ assignedTo }),
    credentials: 'include',
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to claim bug: ${response.status}`);
}

export async function fetchTeamMembers(): Promise<TeamMember[]> {
  const response = await fetch('/api/bugs/team-members', { credentials: 'include' });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch team members: ${response.status}`);
  const data = await response.json();
  return data.members;
}

export interface UpdateBugStateInput {
  state: string;
  reason: string;
}

export interface UpdateBugStateResponse {
  id: number;
  state: string;
  reason: string;
}

export async function updateBugState(
  id: number,
  input: UpdateBugStateInput,
): Promise<UpdateBugStateResponse> {
  const response = await fetch(`/api/bugs/${id}/state`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
    credentials: 'include',
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(
      error.message ?? `Failed to update bug state: ${response.status}`,
    );
  }
  return response.json();
}

export async function fetchMyBugs(params: {
  page?: number;
  pageSize?: number;
}): Promise<BugListResponse> {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('pageSize', String(params.pageSize));

  const response = await fetch(`/api/bugs/mine?${searchParams}`, { credentials: 'include' });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch my tickets: ${response.status}`);
  return response.json();
}

export async function fetchBugComments(id: number): Promise<BugCommentsResponse> {
  const response = await fetch(`/api/bugs/${id}/comments`, { credentials: 'include' });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch comments: ${response.status}`);
  return response.json();
}

export async function addBugComment(
  id: number,
  text: string,
): Promise<BugComment> {
  const response = await fetch(`/api/bugs/${id}/comments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text }),
    credentials: 'include',
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to add comment: ${response.status}`);
  return response.json();
}

export async function fetchBugHistory(id: number): Promise<BugHistoryEntry[]> {
  const response = await fetch(`/api/bugs/${id}/history`, { credentials: 'include' });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch history: ${response.status}`);
  return response.json();
}

export async function fetchBugWorkflow(): Promise<BugWorkflow> {
  const response = await fetch('/api/bugs/workflow', { credentials: 'include' });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) throw new Error(`Failed to fetch workflow: ${response.status}`);
  return response.json();
}

// --- Customer channel (admin-api-gateway tickets; never Azure DevOps comments) ---

/**
 * A failed call of the customer channel, with what the BFF answered: the HTTP
 * status and, when the answer is an error envelope, its catalog `code` and
 * `message`.
 */
export class CustomerConversationError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'CustomerConversationError';
  }
}

export async function fetchCustomerConversation(id: number): Promise<CustomerConversation> {
  const response = await fetch(`/api/bugs/${id}/customer-conversation`, {
    credentials: 'include',
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) {
    throw new Error(`Failed to fetch the customer conversation: ${response.status}`);
  }
  return response.json();
}

export interface CustomerMessageInput {
  body: string;
  attachments: File[];
}

/**
 * Sends a message to the customer. Only the text and the files travel: the
 * author, the tenant and the side of the message come from the session.
 */
export async function sendCustomerMessage(
  id: number,
  input: CustomerMessageInput,
): Promise<void> {
  const formData = new FormData();
  formData.set('body', input.body);
  for (const file of input.attachments) {
    formData.append(TICKET_UPLOAD_FIELDS.ATTACHMENTS, file, file.name);
  }

  const response = await fetch(`/api/bugs/${id}/customer-conversation`, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) {
    const envelope = (await response.json().catch(() => null)) as {
      code?: unknown;
      message?: unknown;
    } | null;
    throw new CustomerConversationError(
      typeof envelope?.message === 'string'
        ? envelope.message
        : `Failed to send the message: ${response.status}`,
      response.status,
      typeof envelope?.code === 'string' ? envelope.code : undefined,
    );
  }
}

/** Download URL of a file of the customer conversation (BFF stream, always a download). */
export function customerAttachmentUrl(id: number, attachmentId: string): string {
  return `/api/bugs/${id}/customer-conversation/attachments/${encodeURIComponent(attachmentId)}`;
}

/** Which of the work items of a page of the bugs list are customer tickets, in one call. */
export async function fetchCustomerTicketFlags(ids: number[]): Promise<CustomerTicketFlag[]> {
  const response = await fetch(`/api/bugs/customer-tickets?ids=${ids.join(',')}`, {
    credentials: 'include',
  });
  assertAuthorizedOrClearSession(response);
  if (!response.ok) {
    throw new Error(`Failed to resolve customer tickets: ${response.status}`);
  }
  return response.json();
}
